import type { ContentAdapterAPI } from "generated/types";
import { observer } from "mobx-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import type { Particle } from "@infinitaslearning/app-player";
import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { trackPlatformEvent } from "analytics/MetabaseTracker/metabase-tracker";
import { STUDENT_MODULES } from "analytics/constants/pageTypes";
import { PLATFORM_EVENT_TYPES } from "analytics/constants/platformEventTypes";
import { PRODUCT_PAGES_ROUTES } from "constants/routes";
import { Heading3 } from "styles/elements/Headings";
import { CssFlex } from "styles/helpers/layout";
import type { PathParams as EdifyModuleDetailsPageParams } from "../../EdifyModuleDetailsPage";
import { StyledButton, StyledDescription, StyledSubjectCard } from "./EdifySubject.styled";

interface ComponentProps {
  className?: string;
  subject: ContentAdapterAPI.EdifyModuleSubjectListItem;
}

export const EdifySubject: React.FC<ComponentProps> = observer(({ className, subject }) => {
  const navigate = useNavigate();
  const { productId, packageId } = useParams() as unknown as EdifyModuleDetailsPageParams;

  const { t } = useTranslation("homepage");

  const onButtonClick = () => {
    trackPlatformEvent({
      module: STUDENT_MODULES.BASIC_PROGRESS,
      itemId: PLATFORM_EVENT_TYPES.STUDENT_MODULES_EVENT_TYPES.CLICK_PRACTICE_SUBJECT,
      value: JSON.stringify({
        productId,
        moduleId: packageId,
        subjectId: subject.id,
        subjectTitle: subject.title,
      }),
    });

    // Redirect to Edify subject details page
    navigate(
      buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_EDIFY_SUBJECT_DETAILS, {
        productId,
        packageId,
        chapterId: subject.id,
      }),
    );
  };

  return (
    <StyledSubjectCard className={className} data-cy="edify-subject-list-item">
      <CssFlex className="tab-title" flexDirection="column" rowGap={0.25}>
        <Heading3 data-cy="edify-subject-title">{subject.title}</Heading3>
        {subject.description && (
          <section data-cy="edify-subject-description">
            <StyledDescription particle={subject.description as unknown as Particle} />
          </section>
        )}
      </CssFlex>
      <StyledButton dataCy="view-subject-button" variant="primary" onClick={onButtonClick}>
        {t("subjects.button.view.label", "View")}
      </StyledButton>
    </StyledSubjectCard>
  );
});
